/**
 * B2BFilterBar Component
 * Horizontal filter bar with dropdowns for:
 * - Product Category
 * - Grade
 * - Certification
 * - Processing Unit
 */

import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { X, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { B2BFilterBarProps } from '../types/b2bMarketplace.types';

/**
 * B2BFilterBar Component
 * Horizontal filter bar with multiple dropdown filters
 */
export const B2BFilterBar: React.FC<B2BFilterBarProps> = ({
  filters,
  onFiltersChange,
  categories,
  grades,
  certifications,
  processingUnits,
  locations,
  className,
}) => {
  const hasActiveFilters =
    filters.category ||
    filters.grade ||
    filters.certification ||
    filters.processingUnit ||
    filters.location ||
    filters.search;

  const handleClearFilters = () => {
    onFiltersChange({
      category: null,
      grade: null,
      certification: null,
      processingUnit: null,
      location: null,
      search: '',
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ search: e.target.value });
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      onFiltersChange({ search: '' });
    }
  };

  return (
    <div className={cn('w-full', className)}>
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 md:gap-4">
        {/* Search Input */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 md:w-5 md:h-5" />
          <Input
            type="text"
            placeholder="Search products..."
            value={filters.search || ''}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            className="w-full pl-10 md:pl-11 pr-4 py-2 md:py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1B6B3C] focus:border-transparent text-sm md:text-base"
          />
          {filters.search && (
            <button
              onClick={() => onFiltersChange({ search: '' })}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Filters Row - Stacked on mobile */}
        <div className="grid grid-cols-2 md:flex md:flex-row gap-2 md:gap-4">
          {/* Category Dropdown */}
          <Select
            value={filters.category || 'all'}
            onValueChange={(value) =>
              onFiltersChange({ category: value === 'all' ? null : value as any })
            }
          >
            <SelectTrigger className="appearance-none w-full px-3 md:px-4 py-2 md:py-2.5 pr-8 md:pr-10 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#1B6B3C] focus:border-transparent cursor-pointer text-sm md:text-base md:min-w-[180px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Grade Dropdown */}
          <Select
            value={filters.grade || 'all'}
            onValueChange={(value) =>
              onFiltersChange({ grade: value === 'all' ? null : value as any })
            }
          >
            <SelectTrigger className="appearance-none w-full px-3 md:px-4 py-2 md:py-2.5 pr-8 md:pr-10 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#1B6B3C] focus:border-transparent cursor-pointer text-sm md:text-base md:min-w-[160px]">
              <SelectValue placeholder="All Grades" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Grades</SelectItem>
              {grades.map((grade) => (
                <SelectItem key={grade.value} value={grade.value}>
                  {grade.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Certification Dropdown */}
          {certifications.length > 0 && (
            <Select
              value={filters.certification || 'all'}
              onValueChange={(value) =>
                onFiltersChange({ certification: value === 'all' ? null : value })
              }
            >
              <SelectTrigger className="appearance-none w-full px-3 md:px-4 py-2 md:py-2.5 pr-8 md:pr-10 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#1B6B3C] focus:border-transparent cursor-pointer text-sm md:text-base md:min-w-[180px]">
                <SelectValue placeholder="All Certifications" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Certifications</SelectItem>
                {certifications.map((cert) => (
                  <SelectItem key={cert.value} value={cert.value}>
                    {cert.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Processing Unit Dropdown */}
          {processingUnits.length > 0 && (
            <Select
              value={filters.processingUnit || 'all'}
              onValueChange={(value) =>
                onFiltersChange({ processingUnit: value === 'all' ? null : value })
              }
            >
              <SelectTrigger className="appearance-none w-full px-3 md:px-4 py-2 md:py-2.5 pr-8 md:pr-10 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#1B6B3C] focus:border-transparent cursor-pointer text-sm md:text-base md:min-w-[180px]">
                <SelectValue placeholder="All Processing Units" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Processing Units</SelectItem>
                {processingUnits.map((pu) => (
                  <SelectItem key={pu.value} value={pu.value}>
                    {pu.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Location Dropdown */}
          <Select
            value={filters.location || 'all'}
            onValueChange={(value) =>
              onFiltersChange({ location: value === 'all' ? null : value })
            }
          >
            <SelectTrigger className="appearance-none w-full px-3 md:px-4 py-2 md:py-2.5 pr-8 md:pr-10 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#1B6B3C] focus:border-transparent cursor-pointer text-sm md:text-base md:min-w-[180px]">
              <SelectValue placeholder="All Locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {locations.map((loc) => (
                <SelectItem key={loc.value} value={loc.value}>
                  {loc.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Search Button */}
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="col-span-2 md:col-span-1 bg-[#FF8C00] hover:bg-[#E67E00] text-white px-6 md:px-8 py-2 md:py-2.5 rounded-md font-semibold transition-all text-sm md:text-base"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default B2BFilterBar;
